import { AmountType, DiscountType } from '@interfaces/discountCodes';
import { create } from 'zustand';

export type RefundableStatus = 'loading' | 'ready' | 'unavailable';

interface CheckoutStore {
  refundableCartId: number | undefined;
  quotedRefundableCartId: number | undefined;
  refundableStatus: RefundableStatus;
  refundableToken: string | null;
  refundablePrice: number;
  refundableAccepted: boolean;
  quotedRefundableAccepted: boolean;
  isRefundableSelected: boolean;
  activateRefundableCart: (cartId: number | undefined) => void;
  markRefundableUnavailable: () => void;
  hotelDatesMismatchAcknowledged: boolean;
  acknowledgeHotelDatesMismatch: () => void;
  resetHotelDatesMismatchAcknowledgement: () => void;
  discount: {
    discountType: DiscountType;
    amount: number;
    amountType: AmountType;
    id: string;
  } | null;
  setDiscount: (discount: {
    discountType: DiscountType;
    amount: number;
    amountType: AmountType;
    id: string;
  }) => void;
  resetDiscount: () => void;
  bonusMarkupType: 'fixed' | 'percentage' | null;
  bonusMarkupValue: number | null;
  setBonusMarkup: (
    type: 'fixed' | 'percentage' | null,
    value: number | null
  ) => void;
  assignedAgent: AssignedAgent | null;
  setAssignedAgent: (agent: AssignedAgent | null) => void;
}

export type AssignedAgent = {
  userId: number;
  name: string;
  email: string | null;
  source: 'agency' | 'affiliate';
  ticketCommissionRate: number;
  hotelCommissionRate: number;
};

export const useCheckoutStore = create<CheckoutStore>((set, get) => ({
  refundableCartId: undefined,
  quotedRefundableCartId: undefined,
  refundableStatus: 'loading',
  refundableToken: null,
  refundablePrice: 0,
  refundableAccepted: false,
  quotedRefundableAccepted: false,
  isRefundableSelected: false,
  activateRefundableCart: (cartId) => {
    if (get().refundableCartId === cartId) return;

    set({
      refundableCartId: cartId,
      quotedRefundableCartId: undefined,
      refundableStatus: 'loading',
      refundableToken: null,
      refundablePrice: 0,
      refundableAccepted: false,
      quotedRefundableAccepted: false,
      isRefundableSelected: false,
    });
  },
  markRefundableUnavailable: () =>
    set({
      refundableStatus: 'unavailable',
      refundableToken: null,
      refundablePrice: 0,
      refundableAccepted: false,
      quotedRefundableAccepted: false,
      isRefundableSelected: false,
    }),
  hotelDatesMismatchAcknowledged: false,
  acknowledgeHotelDatesMismatch: () =>
    set({ hotelDatesMismatchAcknowledged: true }),
  resetHotelDatesMismatchAcknowledgement: () =>
    set({ hotelDatesMismatchAcknowledged: false }),
  discount: null,
  setDiscount: (discount) => set({ discount }),
  resetDiscount: () => set({ discount: null }),
  bonusMarkupType: 'fixed',
  bonusMarkupValue: null,
  setBonusMarkup: (bonusMarkupType, bonusMarkupValue) =>
    set({ bonusMarkupType, bonusMarkupValue }),
  assignedAgent: null,
  setAssignedAgent: (assignedAgent) => set({ assignedAgent }),
}));
